import { PhysioLayout } from "@/components/layout/physio-layout";
import { Building2, CheckCircle } from "lucide-react";

const FRAMEWORKS = [
  {
    title: "NHS Leadership Academy",
    programmes: [
      { name: "Edward Jenner Programme", level: "Foundation", desc: "Online leadership foundation for all NHS staff. Free to all." },
      { name: "Mary Seacole Programme", level: "Aspiring", desc: "For first-time or aspiring leaders. Blended learning." },
      { name: "Elizabeth Garrett Anderson", level: "Developing", desc: "Healthcare leadership MSc-level programme for mid-career managers." },
      { name: "Nye Bevan Programme", level: "Senior", desc: "Executive leadership for senior leaders at Band 8c and above." },
    ],
  },
  {
    title: "NHS Graduate Management Training Scheme",
    programmes: [
      { name: "General Management", level: "Graduate", desc: "2-year accelerated NHS management training scheme. Open to graduates and healthcare professionals." },
      { name: "Health Informatics", level: "Graduate", desc: "Digital and informatics leadership track within NHS GMTS." },
    ],
  },
];

const LEADERSHIP_COMPETENCIES = [
  "Service planning and operational management",
  "Staff management, appraisal, and workforce development",
  "Budget management and NHS financial frameworks",
  "Healthcare governance, risk management, and CQC compliance",
  "Stakeholder management and multi-agency working",
  "Equality, diversity, and inclusion in leadership practice",
  "Change management and quality improvement methodologies (PDSA, Lean, QSIR)",
  "Data literacy and performance management in healthcare",
];

export default function PhysioLeadership() {
  return (
    <PhysioLayout title="Leadership & Management Career">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-teal-700 to-teal-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Building2 className="h-8 w-8 text-teal-300" />
            <div>
              <h2 className="text-xl font-bold">Leadership & Management Development</h2>
              <p className="text-teal-300 text-sm">NHS Leadership Academy · Head of Service · Senior Management</p>
            </div>
          </div>
          <p className="text-teal-200 text-sm leading-relaxed">
            Clinical leadership and management skills are essential for senior physiotherapy roles. This guide covers NHS leadership development programmes, competencies for Head of Service and management roles, and pathways to senior leadership.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Core Leadership Competencies</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {LEADERSHIP_COMPETENCIES.map((comp) => (
              <li key={comp} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle className="h-4 w-4 text-teal-500 shrink-0 mt-0.5" />
                {comp}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-4">
          {FRAMEWORKS.map((framework) => (
            <div key={framework.title} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-3">{framework.title}</h3>
              <div className="space-y-3">
                {framework.programmes.map((prog) => (
                  <div key={prog.name} className="flex items-start gap-3 p-3 bg-teal-50 border border-teal-100 rounded-xl">
                    <span className="text-xs font-bold text-teal-700 bg-teal-100 px-2 py-1 rounded-full shrink-0">{prog.level}</span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{prog.name}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{prog.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Head of Service / Clinical Lead Pathway</h3>
          <div className="space-y-3">
            {[
              { step: "Band 7", action: "Begin leadership development — Edward Jenner, Mary Seacole, or trust leadership programme. Lead a defined clinical service or work stream." },
              { step: "Band 8a", action: "Clinical or operational lead for physiotherapy service. Deputise for Head of Service. Develop QI and governance competencies." },
              { step: "Band 8b", action: "Head of Physiotherapy or Clinical Service Lead. Full operational, financial, and workforce responsibility for physiotherapy service." },
              { step: "Band 8c/d", description: "Director of Therapies / Head of AHP Services. Strategic and executive leadership across multiple therapy professions." },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4 p-3 bg-slate-50 rounded-xl">
                <span className="text-xs font-bold bg-teal-100 text-teal-700 px-2 py-1 rounded-full shrink-0">{item.step}</span>
                <p className="text-sm text-slate-700 leading-relaxed">{item.action ?? item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5">
          <h3 className="font-bold text-slate-900 mb-2">Quality Improvement Tools for Clinical Leaders</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
            {[
              "Plan-Do-Study-Act (PDSA) cycles",
              "NHS QSIR (Quality, Service Improvement and Redesign) toolkit",
              "Lean methodology and process mapping",
              "Driver diagrams and logic models",
              "Statistical Process Control (SPC) charts",
              "NHS Improvement Model for Change",
              "Patient experience measurement (FFT, PROMS, PREMS)",
              "Model Hospital / Getting It Right First Time (GIRFT)",
            ].map((tool) => (
              <div key={tool} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0 mt-2" />
                {tool}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
